import {JsonAdapter} from "../helpers/JsonAdapter";
import {UserProfile} from "./UserProfile";
import dayjs from 'dayjs';
import {IndustrialSector} from "./IndustrialSector";
import {OpportunityType} from "./OpportunityType";
import {Location} from "./Location";
import {WorkExperience} from "./WorkExperience";
import {Skill} from "./Skill";
import {Portfolio} from "./Portfolio";
import {ToolkitStage} from "./ToolkitStage";
import {Language} from "./Language";
import {DiaryFile} from "./DiaryFile";
import {AreaOfInterest} from "./AreaOfInterest";
import {QualificationType} from "./QualificationType";
import {TypeOfWork} from "./TypeOfWork";
import {InterestAlert} from "./InterestAlert";

export class IndividualProfile extends UserProfile {

  qualification_type_other: string = "";
  subject_field_course: string = "";
  start_year: string = "";
  graduation_year: string = "";
  mobile: string = "";
  postcode: string = "";
  date_of_birth: string = "";
  course: any;
  preferred_job_title: string = "";
  industrial_sector: IndustrialSector[] = [];
  candidate_interested_in: OpportunityType[] = [];
  prepared_to_work: Location[] = [];
  area_of_interest: AreaOfInterest[] = [];
  preferred_type_of_work: TypeOfWork[] = [];
  qualifications: QualificationType[] = [];
  qualification_type: QualificationType[] = [];
  qualifications_other = "";
  other_qualifications_memberships = "";
  override published_at: string = "";
  override cv_uploaded: boolean = false;
  cv: any = null;
  override cv_url: string = "";
  work_experience: WorkExperience[] = [];
  skills: Skill[] = [];
  traits_and_attributes: Skill[] = [];
  language: Language[] = [];
  portfolio: Portfolio | null = null;
  stage: ToolkitStage | null = null;
  stages: ToolkitStage[] = [];
  number_of_employer_messages_received: number = 0;
  number_of_projects: number = 0;
  number_of_vacancies_applied: number = 0;
  number_of_vacancies_viewed: number = 0;
  cover_image?: DiaryFile | null = null;
  profile: string = "";
  _current_user_meta!: any;
  professional_membership = "";
  preferred_work_location: Location[] = [];
  preferred_distance: number = 0;
  available_from: string | null = null;
  availability_to_be_confirmed: boolean = false;
  relocation: boolean = false;
  anonymous: boolean = false;

  show_details_in_list: boolean = false;
  required_for_publish: any = null;
  status_of_required_fields: any = null;

  following_companies: any[] = [];

  displayname: string = "";

  static override create(object:any): IndividualProfile {
    let item = Object.assign(new IndividualProfile, super.create(object));
    item = JsonAdapter.fill(item, object);

    if(object.start_year) {
      item.start_year = dayjs(item.start_year).year().toString();
    }

    if(object.graduation_year) {
      item.graduation_year = dayjs(item.graduation_year).year().toString();
    }

    if(object.prepared_to_work) {
      item.prepared_to_work = Location.createMultiple(object.prepared_to_work);
    }

    if(object.candidate_interested_in) {
      item.candidate_interested_in = OpportunityType.createMultiple(object.candidate_interested_in);
    }

    if(object.industrial_sector) {
      item.industrial_sector = IndustrialSector.createMultiple(object.industrial_sector);
    }

    if(object.qualifications) {
      item.qualifications = QualificationType.createMultiple(object.qualifications);
    }

    if(object.preferred_type_of_work) {
      item.preferred_type_of_work = TypeOfWork.createMultiple(object.preferred_type_of_work);
    }

    if(object.qualification_type) {
      item.qualification_type = QualificationType.createMultiple(object.qualification_type);
    }

    if(object.preferred_work_location) {
      item.preferred_work_location = Location.createMultiple(object.preferred_work_location);
    }

    if(object.area_of_interest) {
      item.area_of_interest = AreaOfInterest.createMultiple(object.area_of_interest);
    }

    if(object.work_experience) {
      item.work_experience = WorkExperience.createMultiple(object.work_experience);
    }

    if(object.following_companies) {
      item.following_companies = object.following_companies;
    }

    if(object.cover_image && object.cover_image.id) {
      item.cover_image = DiaryFile.create(object.cover_image);
    } else {
      item.cover_image = null;
    }

    if(object.skills) {
      item.traits_and_attributes = [];
      item.skills = [];
      for(let i = 0; i<object.skills.length; i++) {
        if(object.skills[i].skills_category?.slug === 'traits_and_attributes') {
          item.traits_and_attributes.push(Skill.create(object.skills[i]));
        } else {
          item.skills.push(Skill.create(object.skills[i]));
        }
      }
    }

    if(object.language) {
      item.language = Language.createMultiple(object.language);
    }

    if(object.cv) {
      item.cv = object.cv;
    }

    if(object.stage) {
      item.stage = ToolkitStage.create(object.stage);
    }

    if(object.stages) {
      item.stages = ToolkitStage.createMultiple(object.stages);
    }

    item.course = object.course;

    if(object._current_user_meta) {
      item._current_user_meta = object._current_user_meta;

      if(item._current_user_meta.company_expressed_interest) {
        item._current_user_meta.company_expressed_interest = InterestAlert.createMultiple(item._current_user_meta.company_expressed_interest);
      }
    }

    item.required_for_publish = object.required_for_publish;
    item.status_of_required_fields = object.status_of_required_fields;

    if(object.available_from) {
      item.available_from = object.available_from;
    }

    return item;
  }

  static override createMultiple(objects: IndividualProfile[]): IndividualProfile[] {
    const items = [];
    for (let i = 0; i < objects.length; i++) {
      items.push(IndividualProfile.create(objects[i]));
    }
    return items;
  }

  get courseName() {
    return this.course?.name || '';
  }

  get url() {
    return "/profile/" + this.id;
  }

  override get fullName() {
    return this.displayname || super.fullName;
  }

  get graduationPeriod() {
    return this.start_year + ' - ' + (this.graduation_year ? this.graduation_year : 'Present');
  }

  get isEducationCompleted() {
    return this.qualification_type_other && this.subject_field_course && this.start_year;
  }

  get isJobPreferencesCompleted() {
    return true; // this.preferred_job_title && this.candidate_interested_in.length && this.industrial_sector.length && this.prepared_to_work.length > 0;
  }

  get isCVCompleted() {
    return this.cv_uploaded;
  }

  get isWorkExperiencesCompleted() {
    return this.work_experience.length
  }

  get isPortfolioCompleted() {
    return this.portfolio !== null;
  }

  get isSkillsCompleted() {
    return this.skills.length > 0 && this.language.length > 0;
  }

  get profileProgress() {
    let progress = 0;

    if(this.isEducationCompleted) {
      progress += 30;
    }

    if(this.isSkillsCompleted) {
      progress += 10;
    }

    /*if(this.isJobPreferencesCompleted) {
      progress += 20;
    }*/

    if(this.isWorkExperiencesCompleted) {
      progress += 20;
    }

    if(this.isCVCompleted) {
      progress += 20;
    }

    if(this.isPortfolioCompleted) {
      progress += 20;
    }

    return progress;
  }

  get isProfileCompleted() {
    return this.profileProgress === 100;
  }

  get lastUpdated() {
    if(dayjs(this.updated_at).isBefore(dayjs().subtract(6, 'month'))) {
      return "Over 6 months";
    } else if(dayjs(this.updated_at).isBefore(dayjs().subtract(3, 'month'))) {
      return "Within the last 6 months";
    } else if(dayjs(this.updated_at).isBefore(dayjs().subtract(1, 'month'))) {
      return "Within the last 3 months";
    } else {
      return "Within the last month";
    }
  }

  isCompletedInChecklist(params: any) {
    if (!this.stages.length) {
      return false;
    }

    return this.stages.find(stage => stage.stage === params.stage)?.checklist.find(item => item.slug === params.slug)?.checked;
  }

  isFavourite() {
    return this._current_user_meta && this._current_user_meta.added_to_favourites;
  }

  get isIspCompleted() {
    if(!this.stages.length) {
      return false;
    }

    return this.stages[0].checklist.find(item => item.slug === 'complete_isp')?.checked;
  }

  get portfolioStatus() {
    return 'Pending';
  }

  get vacanciesViewed() {
    return 20;
  }

  get cover() {
    if(this.cover_image && this.cover_image.id) {
      return this.cover_image.url;
    }

    return "/assets/images/covers/individual_profile_image_placeholder.jpg";
  }

  get languagesSeparatedByComma() {
    return this.language.map((item) => item.name).join(', ');
  }

  commaSeparatedProperty(property: keyof IndividualProfile) {
    return this[property].map((item: any) => item.name).join(', ');
  }

  get displayedAvailability() {
    if(this.availability_to_be_confirmed || !this.available_from) {
      return 'To be confirmed';
    }

    return dayjs(this.available_from).format('DD/MM/YYYY');
  }

  get locationList() {
    return this.preferred_work_location.map(location => location.name).join(', ');
  }

  isSubscribed(organisationId: string) {
    return this.following_companies.find((item: any) => item.organisation?.id === organisationId);
  }

  hasOrganisationRelatedActivity(organisationId: string) {
    return this._current_user_meta.applications_to_my_vacancies?.length ||
      this._current_user_meta.favourites_to_my_vacancies ||
      this._current_user_meta.company_expressed_interest?.length ||
      this.isSubscribed(organisationId);
  }

  get name() {
    return this.fullName;
  }
}
